'use client';
import React, { useEffect, useId, useState } from 'react';

interface WindsockProps {
  windSpeed: number;
  windDirection: number;
  gustSpeed?: number;
}

// Returns how many segments are "lifted" (0-5) based on wind speed
function getLiftedSegments(speed: number): number {
  if (speed < 3) return 0;
  if (speed < 8) return 1;
  if (speed < 13) return 2;
  if (speed < 18) return 3;
  if (speed < 23) return 4;
  return 5;
}

// 0° = horizontal (right). 90° = vertical (down).
function getSegmentAngle(segIndex: number, liftedCount: number): number {
  if (liftedCount <= 0) {
    return 88;
  }
  if (segIndex < liftedCount) {
    return 0;
  }
  const droopIndex = segIndex - liftedCount;
  return Math.min(88, 48 + droopIndex * 14);
}

// Compute mini segment positions purely from index (no mutable state)
function getMiniSegments(lifted: number, poleX: number, mountY: number, segLen: number) {
  const segs: { x1: number; y1: number; x2: number; y2: number }[] = [];
  let cx = poleX;
  let cy = mountY;
  for (let i = 0; i < 5; i++) {
    const angle = i < lifted ? 0 : 90;
    const rad = (angle * Math.PI) / 180;
    const x1 = cx;
    const y1 = cy;
    const x2 = cx + segLen * Math.cos(rad);
    const y2 = cy + segLen * Math.sin(rad);
    segs.push({ x1, y1, x2, y2 });
    cx = x2;
    cy = y2;
  }
  return segs;
}

export default function WindsockIndicator({ windSpeed, windDirection, gustSpeed = 0 }: WindsockProps) {
  const rawId = useId();
  const skyGradId = `windsockSkyGrad-${rawId.replace(/:/g, '')}`;
  const [mounted, setMounted] = useState(false);
  const [wobble, setWobble] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (windSpeed <= 0) return;
    const interval = setInterval(() => {
      setWobble(prev => (prev + 1) % 360);
    }, 80);
    return () => clearInterval(interval);
  }, [windSpeed]);

  const liftedSegments = getLiftedSegments(windSpeed);

  const windLabel =
    windSpeed < 3  ? 'CALME' :
    windSpeed < 8  ? 'VENT LÉGER' :
    windSpeed < 13 ? 'VENT MODÉRÉ' :
    windSpeed < 18 ? 'VENT SOUTENU' :
    windSpeed < 23 ? 'VENT FORT' : 'VENT TRÈS FORT';

  const labelColor =
    windSpeed < 3  ? 'text-muted-foreground' :
    windSpeed < 13 ? 'text-vfr' :
    windSpeed < 23 ? 'text-caution' : 'text-ifr';

  const labelBg =
    windSpeed < 3  ? 'bg-slate-100' :
    windSpeed < 13 ? 'bg-green-50' :
    windSpeed < 23 ? 'bg-amber-50' : 'bg-red-50';

  const poleX = 60;
  const poleTopY = 18;
  const mountY = 45;
  const segmentLength = 18;
  const colors = ['#DC2626', '#FFFFFF', '#DC2626', '#FFFFFF', '#DC2626'];
  const sockDirection = (windDirection + 180) % 360;

  // Build segment positions with ground clamp
  const segments: { x1: number; y1: number; x2: number; y2: number; w1: number; w2: number; color: string }[] = [];
  let curX = poleX;
  let curY = mountY;

  for (let i = 0; i < 5; i++) {
    const targetAngle = getSegmentAngle(i, liftedSegments);
    const wobblePart = mounted && windSpeed > 0 && i >= liftedSegments - 1
      ? Math.sin((wobble + i * 20) * 0.12) * 1.5
      : 0;
    const angle = targetAngle + wobblePart;
    const rad = (angle * Math.PI) / 180;
    const dx = segmentLength * Math.cos(rad);
    const dy = segmentLength * Math.sin(rad);
    const w1 = Math.max(2, 10 - i * 1.5);
    const w2 = Math.max(2, 10 - (i + 1) * 1.5);
    segments.push({
      x1: curX,
      y1: curY,
      x2: curX + dx,
      y2: Math.min(curY + dy, 108),
      w1,
      w2,
      color: colors[i],
    });
    curX += dx;
    curY = Math.min(curY + dy, 108);
  }

  // Stable placeholder rendered on server and before client mount
  if (!mounted) {
    return (
      <div className="flex flex-col items-center gap-2 w-full">
        <svg width="130" height="130" viewBox="0 0 130 130">
          <defs>
            <linearGradient id={skyGradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#EBF4FF"/>
              <stop offset="100%" stopColor="#F0F9FF"/>
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="130" height="130" fill={`url(#${skyGradId})`} rx="12"/>
          <line x1="10" y1="115" x2="120" y2="115" stroke="#CBD5E1" strokeWidth="1.5"/>
          <rect x="56" y="110" width="8" height="8" rx="2" fill="#94A3B8"/>
          <line x1={poleX} y1="115" x2={poleX} y2={poleTopY} stroke="#64748B" strokeWidth="3" strokeLinecap="round"/>
          <circle cx={poleX} cy={poleTopY} r="3.5" fill="#475569"/>
          <circle cx={poleX} cy={mountY} r="5" fill="none" stroke="#64748B" strokeWidth="2"/>
          <circle cx={poleX} cy={mountY} r="2" fill="#64748B"/>
        </svg>
        <div className="flex items-center gap-3">
          <div className="text-center">
            <div className="text-2xl font-black font-tabular text-foreground leading-none">{windSpeed} kt</div>
            {gustSpeed > 0 && <div className="text-xs text-muted-foreground font-semibold">raf. {gustSpeed} kt</div>}
          </div>
          <div className={`px-2 py-1 rounded-lg ${labelBg} flex items-center gap-1`}>
            <span className={`text-xs font-bold ${labelColor}`}>{windLabel}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      {/* Main windsock SVG */}
      <svg width="130" height="130" viewBox="0 0 130 130">
        <defs>
          <linearGradient id={skyGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EBF4FF"/>
            <stop offset="100%" stopColor="#F0F9FF"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="130" height="130" fill={`url(#${skyGradId})`} rx="12"/>

        {/* Ground line */}
        <line x1="10" y1="115" x2="120" y2="115" stroke="#CBD5E1" strokeWidth="1.5"/>

        {/* Pole base */}
        <rect x="56" y="110" width="8" height="8" rx="2" fill="#94A3B8"/>
        {/* Pole */}
        <line x1={poleX} y1="115" x2={poleX} y2={poleTopY} stroke="#64748B" strokeWidth="3" strokeLinecap="round"/>
        {/* Pole top cap */}
        <circle cx={poleX} cy={poleTopY} r="3.5" fill="#475569"/>
        {/* Mount ring */}
        <circle cx={poleX} cy={mountY} r="5" fill="none" stroke="#64748B" strokeWidth="2"/>
        <circle cx={poleX} cy={mountY} r="2" fill="#64748B"/>

        {/* Windsock segments */}
        <g
          transform={`rotate(${sockDirection - 90}, ${poleX}, ${mountY})`}
          style={{ transition: 'transform 700ms ease' }}
        >
          {segments.map((seg, i) => {
            const dx = seg.x2 - seg.x1;
            const dy = seg.y2 - seg.y1;
            const len = Math.sqrt(dx * dx + dy * dy);
            const nx = -dy / len;
            const ny = dx / len;
            const pts = [
              `${seg.x1 + nx * seg.w1 / 2},${seg.y1 + ny * seg.w1 / 2}`,
              `${seg.x1 - nx * seg.w1 / 2},${seg.y1 - ny * seg.w1 / 2}`,
              `${seg.x2 - nx * seg.w2 / 2},${seg.y2 - ny * seg.w2 / 2}`,
              `${seg.x2 + nx * seg.w2 / 2},${seg.y2 + ny * seg.w2 / 2}`,
            ].join(' ');
            return (
              <g key={i}>
                <polygon points={pts} fill={seg.color} stroke={seg.color === '#FFFFFF' ? '#CBD5E1' : 'none'} strokeWidth="0.5"/>
              </g>
            );
          })}
        </g>

        {/* Wind direction label */}
        <text x="105" y="60" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E3A5F">VENT DU</text>
        <text x="105" y="73" textAnchor="middle" fontSize="11" fontWeight="800" fill="#2563EB" style={{fontVariantNumeric: "tabular-nums"}}>{windDirection}°</text>
        <text x="105" y="86" textAnchor="middle" fontSize="8" fontWeight="600" fill="#64748B">pointe {sockDirection}°</text>
      </svg>

      {/* Speed info */}
      <div className="flex items-center gap-3">
        <div className="text-center">
          <div className="text-2xl font-black font-tabular text-foreground leading-none">{windSpeed} kt</div>
          {gustSpeed > 0 && <div className="text-xs text-muted-foreground font-semibold">raf. {gustSpeed} kt</div>}
        </div>
        <div className={`px-2 py-1 rounded-lg ${labelBg} flex items-center gap-1`}>
          <svg className={`w-3.5 h-3.5 ${labelColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/>
          </svg>
          <span className={`text-xs font-bold ${labelColor}`}>{windLabel}</span>
        </div>
      </div>

      {/* Visual scale — mini windsocks legend */}
      <div className="flex items-end gap-2 px-1">
        {[0, 5, 10, 15, 20, 25].map((kt) => {
          const lifted = getLiftedSegments(kt);
          const isActive =
            kt === 25
              ? windSpeed >= 23
              : windSpeed >= kt && windSpeed < kt + 5;

          const miniPoleX = 4;
          const miniMountY = 7;
          const miniLen = 3;

          // Compute all mini segment positions purely (no mutable cursor in JSX)
          const miniSegs = getMiniSegments(lifted, miniPoleX, miniMountY, miniLen);

          return (
            <div key={kt} className="flex flex-col items-center gap-0.5">
              <svg width="20" height="18" viewBox="0 0 20 18">
                {/* Mini pole */}
                <line
                  x1={miniPoleX}
                  y1="2"
                  x2={miniPoleX}
                  y2="16"
                  stroke={isActive ? '#2563EB' : '#CBD5E1'}
                  strokeWidth="1.4"
                />
                {miniSegs.map((seg, i) => (
                  <line
                    key={i}
                    x1={seg.x1}
                    y1={seg.y1}
                    x2={seg.x2}
                    y2={seg.y2}
                    stroke={isActive ? (i % 2 === 0 ? '#DC2626' : '#64748B') : '#CBD5E1'}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                ))}
              </svg>
              <span
                className={`text-xs font-semibold ${isActive ? 'text-blue-600' : 'text-muted-foreground'}`}
                style={{ fontSize: '0.6rem' }}
              >
                {kt === 25 ? '25+' : kt}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}